import { useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { CancelButton } from "../components/CancelDialog";

export default function ContactForm() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const type = params.get("tipo") === "think_relative" ? "think_relative" : "have_information";

  const [form, setForm] = useState({
    requester_first_name: "", requester_last_name: "",
    requester_phone: "", requester_email: "", relationship_claim: "",
    message: "", match_details: "", approximate_location_text: "", info_datetime: "",
    good_faith_confirmation: false, privacy_accepted: false,
    responsibility_accepted: false, share_contact_consent: false,
  });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [trackingCode, setTrackingCode] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/cases/${code}/contact`, { ...form, request_type: type });
      setTrackingCode(data?.tracking_code || "");
      setDone(true);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto text-center py-10 space-y-4" data-testid="contact-success">
        <CheckCircle2 className="w-14 h-14 text-[#047857] mx-auto" />
        <h1 className="font-heading text-2xl font-bold">Información enviada</h1>
        <p className="text-[#475569]">
          Tu información fue enviada de forma privada al reportante. No será publicada. Tú decides si te comunicas y si compartes tu contacto.
        </p>
        {trackingCode && (
          <div className="rounded-xl border-2 border-[#FFD100] bg-[#FFFBEB] p-4 space-y-2 text-left" data-testid="contact-tracking-code">
            <p className="text-sm font-semibold text-[#7A5E00]">Código de seguimiento</p>
            <div className="flex items-center gap-2">
              <code className="text-lg font-mono font-bold tracking-wider" data-testid="tracking-code-value">{trackingCode}</code>
              <button onClick={() => { navigator.clipboard?.writeText(trackingCode); }} data-testid="copy-tracking-code"
                className="h-8 px-2 rounded-lg bg-white border border-[#E5E7EB] text-xs font-medium">Copiar</button>
            </div>
            <p className="text-xs text-[#8A6D00]">Guarda este código. Podrás usarlo en <b>“Consultar solicitud”</b> para revisar si el reportante respondió. Si lo pierdes, podrías no volver a acceder al seguimiento.</p>
            <Link to="/consultar-solicitud" data-testid="contact-lookup-link" className="inline-block text-sm text-[#003893] font-semibold underline">Consultar solicitud</Link>
          </div>
        )}
        <Link to={`/caso/${code}`} className="inline-block h-12 px-6 leading-[3rem] rounded-xl bg-[#1E3A8A] text-white font-semibold">
          Volver al caso
        </Link>
      </div>
    );
  }

  const isRelative = type === "think_relative";

  return (
    <div className="max-w-lg mx-auto space-y-5">
      <h1 className="font-heading text-2xl font-bold text-[#0F172A]">
        {isRelative ? "Creo que es mi familiar" : "Tengo información"}
      </h1>
      <div className="flex gap-3 p-4 rounded-xl bg-[#EFF6FF] border border-[#BFDBFE]">
        <ShieldCheck className="w-5 h-5 text-[#1E3A8A] shrink-0 mt-0.5" />
        <p className="text-sm text-[#1E40AF]">Tus datos de contacto se mantienen privados y solo se comparten si tú lo autorizas. Reencuentro facilita el enlace; las partes deciden si se comunican.</p>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <Input label="Tu nombre *" testid="contact-firstname" value={form.requester_first_name} onChange={(v) => set("requester_first_name", v)} required />
        <Input label="Apellido" testid="contact-lastname" value={form.requester_last_name} onChange={(v) => set("requester_last_name", v)} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input label="Teléfono" testid="contact-phone" value={form.requester_phone} onChange={(v) => set("requester_phone", v)} />
          <Input label="Email" testid="contact-email" type="email" value={form.requester_email} onChange={(v) => set("requester_email", v)} />
        </div>
        <p className="text-xs text-[#94A3B8] -mt-2">Proporciona al menos un medio de contacto (teléfono o email).</p>

        {isRelative && (
          <>
            <Input label="Relación familiar *" testid="contact-relationship" value={form.relationship_claim} onChange={(v) => set("relationship_claim", v)} required />
          </>
        )}

        <Textarea label="Mensaje *" testid="contact-message" value={form.message} onChange={(v) => set("message", v)} required />

        {!isRelative && (
          <>
            <Input label="Ubicación aproximada (opcional)" testid="contact-location" value={form.approximate_location_text} onChange={(v) => set("approximate_location_text", v)} />
            <DateField label="Fecha de la información" testid="contact-datetime" max={new Date().toISOString().slice(0, 10)} value={form.info_datetime} onChange={(v) => set("info_datetime", v)} />
          </>
        )}

        <Check testid="contact-goodfaith" checked={form.good_faith_confirmation} onChange={(v) => set("good_faith_confirmation", v)}
          label="Confirmo que esta información es de buena fe y verdadera." />
        <Check testid="contact-privacy" checked={form.privacy_accepted} onChange={(v) => set("privacy_accepted", v)}
          label="Acepto la política de privacidad." />
        <Check testid="contact-responsibility" checked={form.responsibility_accepted} onChange={(v) => set("responsibility_accepted", v)}
          label="Entiendo que Reencuentro facilita un enlace protegido entre personas, pero no verifica identidades, parentescos, custodia legal ni la veracidad de la información recibida. Soy responsable de verificar la información, decidir si deseo comunicarme y asumir las consecuencias de mis acciones u omisiones." />
        <Check testid="contact-share-consent" checked={form.share_contact_consent} onChange={(v) => set("share_contact_consent", v)}
          label="Autorizo compartir mi contacto con el reportante. (opcional)" />

        {error && <p className="text-sm text-[#DC2626]" data-testid="contact-error">{error}</p>}
        <div className="flex gap-3">
          <button type="button" onClick={() => navigate(-1)} data-testid="contact-back-btn"
            className="h-14 px-5 rounded-xl bg-[#F1F5F9] text-[#0F172A] font-semibold">Volver</button>
          <button type="submit" disabled={submitting} data-testid="contact-submit-btn"
            className="flex-1 h-14 rounded-xl bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] disabled:opacity-50">
            {submitting ? "Enviando…" : "Enviar"}
          </button>
        </div>
        <CancelButton testid="contact-cancel-btn" onConfirm={() => navigate("/")} />
      </form>
    </div>
  );
}

export function Input({ label, value, onChange, testid, type = "text", required, placeholder }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#475569]">{label}</span>
      <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]" />
    </label>
  );
}

export function Textarea({ label, value, onChange, testid, required }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#475569]">{label}</span>
      <textarea data-testid={testid} value={value} required={required} rows={3}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]" />
    </label>
  );
}

export function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer">
      <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0" />
      <span className="text-sm text-[#475569]">{label}</span>
    </label>
  );
}

export function DateField({ label, value, onChange, testid, hint, max }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#475569]">{label}</span>
      <input data-testid={testid} type="date" value={value || ""} max={max}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-12 px-3 rounded-lg border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]" />
      {hint && <span className="text-xs text-[#94A3B8]">{hint}</span>}
    </label>
  );
}
